import { Map } from 'immutable';
import memoize from 'lodash/memoize';
import { createSelector } from 'reselect';

import * as PaymentsConstants from './constants';
import * as D from './deps';
import * as ProtectedUtils from './protectedUtils';
import * as PaymentsUtils from './utils';

export function getUserSettings(state) {
  return state.userSettings || Map();
}

export function getUserSettingsPersisted(state) {
  return state.userSettingsPersisted || Map();
}

export function getSource(state) {
  return state.source || Map();
}

export function getSpecialOffers(state) {
  return state.specialOffers || Map();
}

export const getSubscriptionInfoV2 = createSelector(
  getUserSettings,
  ProtectedUtils.getPropFromMap(PaymentsConstants.ReducerKeys.SUBSCRIPTION_INFO_V2, Map()),
);

export const getIsPendingPurchase = createSelector(
  getUserSettings,
  ProtectedUtils.getPropFromMap(PaymentsConstants.ReducerKeys.IS_PENDING_PURCHASE),
);

export const getActiveProductGroup = createSelector(
  getSubscriptionInfoV2,
  ProtectedUtils.getPropFromMap(PaymentsConstants.SubscriptionKeys.ACTIVE_PRODUCT),
);

export const getPriceAtNextBillingDate = createSelector(
  getSubscriptionInfoV2,
  ProtectedUtils.getPropFromMap(PaymentsConstants.SubscriptionKeys.PRICE_AT_NEXT_BILLING_DATE),
);

export const getActiveProductGroupTier = createSelector(
  getActiveProductGroup,
  ProtectedUtils.getPropFromMap(PaymentsConstants.ProductKeys.PRODUCT_TIER),
);

export const getActiveProductType = createSelector(
  getActiveProductGroup,
  ProtectedUtils.getPropFromMap(PaymentsConstants.ProductKeys.PRODUCT_TYPE),
);

export const isActiveProductSpecialOffer = createSelector(
  getActiveProductType,
  ProtectedUtils.isProductTypeSpecialOffer,
);

export const getActiveProductDurationType = createSelector(
  getActiveProductGroup,
  ProtectedUtils.getPropFromMap(PaymentsConstants.ProductKeys.DURATION_TYPE),
);

export const getActiveProductPrice = createSelector(
  getActiveProductGroup,
  ProtectedUtils.getPropFromMap(PaymentsConstants.ProductKeys.PRICE),
);

export const getActiveProductCurrency = createSelector(
  getActiveProductGroup,
  ProtectedUtils.getPropFromMap(PaymentsConstants.ProductKeys.ACCEPTED_CURRENCY),
);

export const getActiveProductDetails = createSelector(
  getActiveProductGroup,
  ProtectedUtils.getPropFromMap(PaymentsConstants.ProductKeys.PRODUCT_DETAILS),
);

export const getActiveProductDiscountType = createSelector(
  getActiveProductGroup,
  ProtectedUtils.getPropFromMap(PaymentsConstants.ProductKeys.DISCOUNT_TYPE),
);

export const isActiveProductDiscountTypeStudent = createSelector(
  getActiveProductDiscountType,
  ProtectedUtils.isEqual(PaymentsConstants.discountTypes.STUDENT),
);

export const getActiveProductDurationTime = createSelector(
  getActiveProductGroup,
  ProtectedUtils.getPropFromMap(PaymentsConstants.ProductKeys.DURATION_TIME),
);

export const getActiveProductTimesRenewable = createSelector(
  getActiveProductGroup,
  ProtectedUtils.getPropFromMap(PaymentsConstants.ProductKeys.TIMES_RENEWABLE, null),
);

export const getRenewalsRemaining = createSelector(
  getSubscriptionInfoV2,
  ProtectedUtils.getPropFromMap(PaymentsConstants.ProductKeys.RENEWALS_REMAINING, null),
);

export const getPaymentProviderType = createSelector(
  getSubscriptionInfoV2,
  ProtectedUtils.getPropFromMap(PaymentsConstants.ProductKeys.PAYMENT_PROVIDER_TYPE),
);

export const productTierIsAdSupported = createSelector(
  getActiveProductGroupTier,
  ProtectedUtils.isEqual(PaymentsConstants.tiers.AD_SUPPORTED),
);

export const productTierIsPlus = createSelector(
  getActiveProductGroupTier,
  ProtectedUtils.isEqual(PaymentsConstants.tiers.PANDORA_PLUS),
);

export const productTierIsPremium = createSelector(
  getActiveProductGroupTier,
  ProtectedUtils.isEqual(PaymentsConstants.tiers.PANDORA_PREMIUM),
);

export const productTierIsFamily = createSelector(
  getActiveProductGroupTier,
  ProtectedUtils.isEqual(PaymentsConstants.tiers.PANDORA_FAMILY),
);

export const hasPremiumPlan = createSelector(
  getActiveProductGroupTier,
  PaymentsUtils.isPremiumPlan,
);

export const isListenerComplimentary = createSelector(
  getActiveProductType,
  ProtectedUtils.isEqual(PaymentsConstants.productTypes.COMPLIMENTARY_TIME),
);

export const getPendingProducts = createSelector(
  getSubscriptionInfoV2,
  ProtectedUtils.getPropFromMap(PaymentsConstants.SubscriptionKeys.PENDING_PRODUCTS),
);

export const getPendingSubscriptionProduct = createSelector(
  getPendingProducts,
  ProtectedUtils.getPendingSubscriptionProduct,
);

export const getPendingSubscriptionPrice = createSelector(
  getPendingSubscriptionProduct,
  ProtectedUtils.getPropFromMap(PaymentsConstants.ProductKeys.PRICE, null),
);

export const getPendingSubscriptionCurrency = createSelector(
  getPendingSubscriptionProduct,
  ProtectedUtils.getPropFromMap(PaymentsConstants.ProductKeys.ACCEPTED_CURRENCY, null),
);

export const getPendingTrial = createSelector(getPendingProducts, ProtectedUtils.getPendingTrial);

export const getPendingGifts = createSelector(getPendingProducts, ProtectedUtils.getPendingGifts);

export const createGetPendingGiftsOnDifferentTier = createSelector(getPendingGifts, pendingGifts =>
  memoize(
    tier => ProtectedUtils.pendingGiftsOnDifferentTier(pendingGifts, tier),
    ProtectedUtils.generateMemoKey,
  ),
);

export const getSubscriptionProduct = createSelector(
  getActiveProductGroup,
  getPendingSubscriptionProduct,
  ProtectedUtils.getSubscriptionProduct,
);

// Active Product ==> The product a listener is actively being served
// Current Product ==> The product a listener will eventually transition to
// Subscription Product ==> The product of type=SUBSCRIPTION in a listener's subscription info
// Pending Subscription Product ==> The first pending product of type=SUBSCRIPTION
export const getCurrentProductGroup = createSelector(
  getActiveProductGroup,
  getPendingSubscriptionProduct,
  ProtectedUtils.getCurrentProduct,
);

export const getCurrentProductDurationType = createSelector(
  getCurrentProductGroup,
  ProtectedUtils.getPropFromMap(PaymentsConstants.ProductKeys.DURATION_TYPE),
);

export const getCurrentProductTier = createSelector(
  getCurrentProductGroup,
  ProtectedUtils.getPropFromMap(PaymentsConstants.ProductKeys.PRODUCT_TIER),
);

export const getCurrentProductDiscountType = createSelector(
  getCurrentProductGroup,
  ProtectedUtils.getPropFromMap(PaymentsConstants.ProductKeys.DISCOUNT_TYPE),
);

export const getCurrentProductDetails = createSelector(
  getCurrentProductGroup,
  ProtectedUtils.getPropFromMap(PaymentsConstants.ProductKeys.PRODUCT_DETAILS),
);

export const getCurrentProductPrice = createSelector(
  getCurrentProductGroup,
  ProtectedUtils.getPropFromMap(PaymentsConstants.ProductKeys.PRICE),
);

export const getSubscriptionPrice = createSelector(
  getSubscriptionProduct,
  ProtectedUtils.getPropFromMap(PaymentsConstants.ProductKeys.PRICE),
);

export const getSubscriptionPaymentProvider = createSelector(
  getSubscriptionProduct,
  ProtectedUtils.getPropFromMap(PaymentsConstants.ProductKeys.PAYMENT_PROVIDER),
);

// @todo [RADIO-27023] We need a backend fix to be completely accurate.
export const getSubscriptionDurationType = createSelector(
  getSubscriptionProduct,
  ProtectedUtils.getPropFromMap(PaymentsConstants.ProductKeys.DURATION_TYPE),
);

export const getSubscriptionDurationTime = createSelector(
  getSubscriptionProduct,
  ProtectedUtils.getPropFromMap(PaymentsConstants.ProductKeys.DURATION_TIME),
);

export function getPendingProductPrice(state) {
  const subscriptionInfo = getSubscriptionInfoV2(state);
  if (!subscriptionInfo.isEmpty()) {
    const pendingProducts = subscriptionInfo.get(
      PaymentsConstants.SubscriptionKeys.PENDING_PRODUCTS,
    );
    if (pendingProducts && pendingProducts.size > 0) {
      const productObject = pendingProducts.toJS();
      return productObject[0].price;
    }
  }
  return null;
}

export const getIsUpdatingBillingInfo = createSelector(
  getUserSettings,
  ProtectedUtils.getPropFromMap(PaymentsConstants.ReducerKeys.UPDATING_BILLING_INFO),
);

export const getPreviousPlan = createSelector(
  getUserSettings,
  ProtectedUtils.getPropFromMap(PaymentsConstants.ReducerKeys.PREVIOUS_PLAN),
);

export const getPreviousTier = createSelector(
  getPreviousPlan,
  ProtectedUtils.getPropFromMap(PaymentsConstants.PlanKeys.TIER, ''),
);

export const getPreviousDiscountType = createSelector(
  getPreviousPlan,
  ProtectedUtils.getPropFromMap(PaymentsConstants.PlanKeys.DISCOUNT_TYPE, ''),
);

export const getIsGiftee = createSelector(
  getSubscriptionInfoV2,
  ProtectedUtils.getPropFromMap(PaymentsConstants.SubscriptionKeys.GIFTEE, false),
);

export const hasUserDowngraded = createSelector(
  getActiveProductGroupTier,
  getCurrentProductTier,
  ProtectedUtils.hasUserDowngraded,
);

export const didChangePlans = createSelector(
  getPreviousTier,
  getPreviousDiscountType,
  getCurrentProductTier,
  getCurrentProductDiscountType,
  ProtectedUtils.didChangePlans,
);

export const getNextBillingDate = createSelector(
  getSubscriptionInfoV2,
  ProtectedUtils.getPropFromMap(PaymentsConstants.SubscriptionKeys.NEXT_BILLING_DATE),
);

export const getIsSwitchingPlans = createSelector(
  getUserSettings,
  ProtectedUtils.getPropFromMap(PaymentsConstants.ReducerKeys.SWITCHING_PLANS),
);

export const getCreditCardFormStatus = createSelector(
  getUserSettings,
  ProtectedUtils.getPropFromMap(PaymentsConstants.ReducerKeys.CREDIT_CARD_FORM_STATUS),
);

export const getServerErrorType = createSelector(
  getUserSettings,
  ProtectedUtils.getPropFromMap(PaymentsConstants.ReducerKeys.SERVER_ERROR_TYPE),
);

export const getServerErrorMessage = createSelector(
  getUserSettings,
  ProtectedUtils.getPropFromMap(PaymentsConstants.ReducerKeys.SERVER_ERROR_MESSAGE),
);

export const getIsPendingPaymentUpdate = createSelector(
  getUserSettings,
  ProtectedUtils.getPropFromMap(PaymentsConstants.ReducerKeys.IS_PENDING_PAYMENT_UPDATE),
);

export const getCreditCardFormValues = createSelector(
  state => state.form && state.form.CreditCardForm,
  ProtectedUtils.getCreditCardFormValues,
);

export const getInPaymentBackedTrial = createSelector(
  getSubscriptionInfoV2,
  ProtectedUtils.getPropFromMap(PaymentsConstants.SubscriptionKeys.IN_PAYMENT_BACKED_TRIAL),
);

export const getTrialEndDate = createSelector(
  getSubscriptionInfoV2,
  ProtectedUtils.getPropFromMap(PaymentsConstants.SubscriptionKeys.TRIAL_END_DATE),
);

export const getActiveProductExpirationDate = createSelector(
  getNextBillingDate,
  getTrialEndDate,
  ProtectedUtils.getActiveProductExpirationDate,
);

// The listener is on a 6 month Premium trial and their active product will renew along with their 6 month Premium trial.
export const is6MPremiumTrialExpiringWithActiveProduct = createSelector(
  getNextBillingDate,
  getTrialEndDate,
  getActiveProductDurationType,
  ProtectedUtils.is6MPremiumTrialExpiringWithActiveProduct,
);

// The listener is on a 6 month Premium trial and their active product renews before their 6 month Premium trial is up.
// This happens if the listener subscribed to the 6mo Premium trial with < 6mo left in their active subscription.
export const is6MPremiumTrialExpiringAfterActiveProduct = createSelector(
  getNextBillingDate,
  getTrialEndDate,
  getActiveProductDurationType,
  ProtectedUtils.is6MPremiumTrialExpiringAfterActiveProduct,
);

export const getPaypalRedirectUrl = createSelector(
  getUserSettings,
  ProtectedUtils.getPropFromMap(PaymentsConstants.ReducerKeys.PAYPAL_REDIRECT_URL),
);

export const getAutoRenew = createSelector(
  getSubscriptionInfoV2,
  ProtectedUtils.getPropFromMap(PaymentsConstants.SubscriptionKeys.AUTO_RENEW),
);

export const getPaymentProvider = createSelector(
  getSubscriptionInfoV2,
  ProtectedUtils.getPropFromMap(PaymentsConstants.SubscriptionKeys.PAYMENT_PROVIDER),
);

export const paymentProviderIsPaypal = createSelector(
  getPaymentProvider,
  ProtectedUtils.isEqual(PaymentsConstants.PaymentProviders.PAYPAL),
);

export const getDisplayablePaymentProvider = createSelector(
  getSubscriptionInfoV2,
  ProtectedUtils.getPropFromMap(PaymentsConstants.SubscriptionKeys.DISPLAYABLE_PAYMENT_PROVIDER),
);

export const isNonPaymentBackedUser = createSelector(
  getIsGiftee,
  getInPaymentBackedTrial,
  ProtectedUtils.isNonPaymentBackedUser,
);

export const getIsAnonymousUpgrade = createSelector(
  getUserSettings,
  ProtectedUtils.getPropFromMap(PaymentsConstants.ReducerKeys.IS_ANONYMOUS_UPGRADE, false),
);

export const getIsPaypalComplete = createSelector(
  getUserSettings,
  ProtectedUtils.getPropFromMap(PaymentsConstants.ReducerKeys.PAYPAL_COMPLETE),
);

export const getUpgradeOfferStatsFields = createSelector(
  getUserSettings,
  ProtectedUtils.getUpgradeOfferStatsFields,
);

export function getPaypalTier(state) {
  return state.paypal.get(PaymentsConstants.ReducerKeys.PP_TIER);
}

export function getPaypalDiscountType(state) {
  return state.paypal.get(PaymentsConstants.ReducerKeys.PP_DISCOUNT_TYPE);
}

export function getPaypalDurationType(state) {
  return state.paypal.get(PaymentsConstants.ReducerKeys.PP_DURATION_TYPE);
}

export function getPaypalSpecialOfferCode(state) {
  return state.paypal.get(PaymentsConstants.ReducerKeys.PP_SPECIAL_OFFER_CODE);
}

export function getPaypalVendorSku(state) {
  return state.paypal.get(PaymentsConstants.ReducerKeys.PP_VENDOR_SKU);
}

export function getPaypalBackingProductIdentifier(state) {
  return state.paypal.get(PaymentsConstants.ReducerKeys.PP_PRODUCT_ID);
}

export const getAvailableProductGroups = createSelector(
  getUserSettings,
  ProtectedUtils.getPropFromMap(PaymentsConstants.ReducerKeys.AVAILABLE_PRODUCTS),
);

export const isCreditCardLoaded = createSelector(
  getUserSettings,
  ProtectedUtils.isEqual(PaymentsConstants.ReducerKeys.CREDIT_CARD_INFO_IS_LOADED, true),
);

export const isSubscriptionInfoLoaded = createSelector(
  getUserSettings,
  ProtectedUtils.isNotEmpty(PaymentsConstants.ReducerKeys.SUBSCRIPTION_INFO_V2),
);

export const isAvailableProductsLoaded = createSelector(
  getUserSettings,
  ProtectedUtils.isNotEqual(PaymentsConstants.ReducerKeys.AVAILABLE_PRODUCTS, null),
);

export const getSelectedPaymentProvider = createSelector(
  getUserSettings,
  ProtectedUtils.getPropFromMap(PaymentsConstants.ReducerKeys.SELECTED_PAYMENT_PROVIDER),
);

export const getSelectedTier = createSelector(
  getUserSettingsPersisted,
  ProtectedUtils.getPropFromMap(PaymentsConstants.ReducerKeys.SELECTED_TIER),
);

export const getSelectedDiscountType = createSelector(
  getUserSettingsPersisted,
  ProtectedUtils.getPropFromMap(PaymentsConstants.ReducerKeys.SELECTED_DISCOUNT_TYPE),
);

export const getSelectedPlanName = createSelector(
  getSelectedTier,
  getSelectedDiscountType,
  PaymentsUtils.getPlanName,
);

export const getCreditCardInfo = createSelector(
  getUserSettings,
  ProtectedUtils.getPropFromMap(PaymentsConstants.ReducerKeys.CREDIT_CARD_INFO),
);

export const getCreditCardInfoCardType = createSelector(
  getCreditCardInfo,
  ProtectedUtils.getPropFromMap(PaymentsConstants.CreditCardKeys.CARD_COMPANY),
);

export const getCreditCardInfoCardNumber = createSelector(
  getCreditCardInfo,
  ProtectedUtils.getPropFromMap(PaymentsConstants.CreditCardKeys.CARD_NUMBER),
);

// used internally, use next selector to normalize result
export const getCreditCardInfoCurrentAmount = createSelector(
  getCreditCardInfo,
  ProtectedUtils.getPropFromMap(PaymentsConstants.CreditCardKeys.CURRENT_AMOUNT),
);

export const getGiftCardRemainingBalance = createSelector(
  getCreditCardInfoCurrentAmount,
  ProtectedUtils.getGiftCardRemainingBalance,
);

export const hasCreditCardOnFile = createSelector(getCreditCardInfo, ProtectedUtils.isNotEmpty());

export const getIsInvalidCreditCard = createSelector(
  getUserSettings,
  ProtectedUtils.getPropFromMap(PaymentsConstants.ReducerKeys.INVALID_CREDIT_CARD),
);

export const hasValidCreditCardOnFile = createSelector(
  hasCreditCardOnFile,
  getIsInvalidCreditCard,
  ProtectedUtils.hasValidCreditCardOnFile,
);

export const isCreditCardDeleted = createSelector(
  getPaymentProvider,
  hasCreditCardOnFile,
  ProtectedUtils.isCreditCardDeleted,
);

export const isListenerSubscribed = createSelector(
  getSubscriptionInfoV2,
  ProtectedUtils.getPropFromMap(PaymentsConstants.SubscriptionKeys.SUBSCRIBER),
);

// Is the user a canceled subscriber, i.e. they have canceled but haven't churned yet?
export const isCurrentListenerCanceledSubscriber = createSelector(
  getAutoRenew,
  isListenerSubscribed,
  getInPaymentBackedTrial,
  ProtectedUtils.isCurrentListenerCanceledSubscriber,
);

export const isTrialEligible = createSelector(
  getAvailableProductGroups,
  ProtectedUtils.isTrialEligible,
);

export function isPremiumTrialEligible(state) {
  return getOfferPaymentBackedTrial(state, PaymentsConstants.tiers.PANDORA_PREMIUM);
}

export const getSpecialOfferProductGroups = createSelector(
  getSpecialOffers,
  ProtectedUtils.getPropFromMap(PaymentsConstants.ReducerKeys.SPECIAL_OFFER_PRODUCTS),
);

export const getOfferedProductGroups = createSelector(
  getSpecialOfferProductGroups,
  getAvailableProductGroups,
  ProtectedUtils.getOfferedProductGroups,
);

export const hasAvailableProducts = createSelector(
  getAvailableProductGroups,
  ProtectedUtils.isNotEmpty(),
);

// Gets a product group given list of product groups, tier, and payment provider, with access to relevant fields
// like initialProduct (equal to offerPaymentBackedTrial), payment provider (store),
// tier, and vendor sku (most important field for passing back to purchase APIs)
export function getProductGroup(
  state,
  tier,
  paymentProvider,
  discountType = PaymentsConstants.discountTypes.NONE,
) {
  const productGroups = getAvailableProductGroups(state);

  if (productGroups) {
    return productGroups.find(group => {
      const groupDiscountType = PaymentsUtils.getDiscountTypeFromProductGroup(group);
      return (
        group.get(PaymentsConstants.ProductKeys.PAYMENT_PROVIDER) === paymentProvider &&
        group.get(PaymentsConstants.ProductKeys.PRODUCT_TIER) === tier &&
        (!groupDiscountType || groupDiscountType === discountType)
      );
    });
  }
  return null;
}

function getPaymentBackedTrialField(
  state,
  tierToCheck,
  defaultValue,
  fieldName,
  discountTypeToCheck = PaymentsConstants.discountTypes.NONE,
) {
  let result = defaultValue;
  const productGroups = getOfferedProductGroups(state);
  if (productGroups) {
    const trialGroup = productGroups.findLast(group => {
      return PaymentsUtils.hasInitialProduct(group, tierToCheck, discountTypeToCheck);
    });
    if (trialGroup) {
      result = trialGroup.getIn([PaymentsConstants.ProductKeys.INITIAL_PRODUCT, fieldName]);
    }
  }
  return result;
}

export function getPaymentBackedTrialLength(
  state,
  tierToCheck,
  discountTypeToCheck = PaymentsConstants.discountTypes.NONE,
) {
  return getPaymentBackedTrialField(
    state,
    tierToCheck,
    0,
    PaymentsConstants.ProductKeys.DURATION_TIME,
    discountTypeToCheck,
  );
}

export function getPaymentBackedTrialDurationType(
  state,
  tierToCheck,
  discountTypeToCheck = PaymentsConstants.discountTypes.NONE,
) {
  return getPaymentBackedTrialField(
    state,
    tierToCheck,
    '',
    PaymentsConstants.ProductKeys.DURATION_TYPE,
    discountTypeToCheck,
  );
}

export const isInTrial = createSelector(
  getActiveProductType,
  ProtectedUtils.isEqual(PaymentsConstants.SubscriptionKeys.TRIAL_SUBSCRIPTION),
);

export const isInPlusTrial = createSelector(
  productTierIsPlus,
  isInTrial,
  ProtectedUtils.isInPlusTrial,
);

export function getOfferPaymentBackedTrial(
  state,
  tierToCheck,
  discountTypeToCheck = PaymentsConstants.discountTypes.NONE,
) {
  if (
    tierToCheck === PaymentsConstants.tiers.PANDORA_FAMILY &&
    !productTierIsAdSupported(state) &&
    !isInPlusTrial(state)
  ) {
    return false;
  }

  const productGroups = getAvailableProductGroups(state);
  if (!productGroups) {
    return false;
  }

  return productGroups.some(productGroup =>
    PaymentsUtils.hasInitialProduct(productGroup, tierToCheck, discountTypeToCheck),
  );
}

export const createGetOfferTrialTypeSelector = createSelector(
  getOfferedProductGroups,
  productGroups =>
    memoize(
      (tier, discountType) => ProtectedUtils.getOfferTrialType(productGroups, tier, discountType),
      ProtectedUtils.generateMemoKey,
    ),
);

// @todo [WEBCLIENT-7294] We should remove `getOfferedTrialProducts` as a selector and instead use selectors that are more specific
export const getOfferedTrialProducts = createSelector(
  getOfferedProductGroups,
  ProtectedUtils.getInitialProductsFromProductGroups,
);

export const getFamilyPlanRecord = createSelector(
  getUserSettings,
  ProtectedUtils.getPropFromMap(PaymentsConstants.ProductKeys.FAMILY_PLAN_RECORD),
);

export const getFamilyPlanType = createSelector(
  getActiveProductDetails,
  ProtectedUtils.getPropFromMap(PaymentsConstants.ProductKeys.FAMILY_PLAN_TYPE),
);

export const isManagingFamilyPlanAccounts = createSelector(
  getUserSettings,
  ProtectedUtils.getPropFromMap(
    PaymentsConstants.SubscriptionKeys.IS_MANAGING_FAMILY_PLAN_ACCOUNTS,
  ),
);

export const isInvitingFamilyPlanAccounts = createSelector(
  getUserSettings,
  ProtectedUtils.getPropFromMap(
    PaymentsConstants.SubscriptionKeys.IS_INVITING_FAMILY_PLAN_ACCOUNTS,
  ),
);

export const isFamilyPlanMemberChild = createSelector(
  getFamilyPlanType,
  ProtectedUtils.isEqual('CHILD'),
);

export const isFamilyPlanMemberParent = createSelector(
  getFamilyPlanType,
  ProtectedUtils.isEqual('PARENT'),
);

export const isAgeIneligible = createSelector(
  getCreditCardFormStatus,
  D.Utils.Listener.getCurrentUserBirthYear,
  ProtectedUtils.isAgeIneligible,
);

export function getPartnerUpgradeState(state) {
  return state.partnerUpgrade || Map();
}

export const getPartnerUpgradeEntitlementId = createSelector(
  getPartnerUpgradeState,
  ProtectedUtils.getPropFromMap(PaymentsConstants.ReducerKeys.PARTNER_UPGRADE_ENTITLEMENT_ID),
);

export const isPartnerUpgradeFlow = createSelector(
  getPartnerUpgradeEntitlementId,
  ProtectedUtils.isPartnerUpgradeFlow,
);

export const getPartnerUpgradePartnerName = createSelector(
  getPartnerUpgradeState,
  ProtectedUtils.getPropFromMap(PaymentsConstants.ReducerKeys.PARTNER_UPGRADE_PARTNER_NAME),
);

export const isPartnerUpgradeUserConfirmed = createSelector(
  getPartnerUpgradeState,
  ProtectedUtils.getPropFromMap(PaymentsConstants.ReducerKeys.PARTNER_UPGRADE_USER_CONFIRMED),
);

export const getPartnerUpgradeTier = createSelector(
  getPartnerUpgradeState,
  ProtectedUtils.getPropFromMap(PaymentsConstants.ReducerKeys.PARTNER_UPGRADE_TIER),
);

export const getPartnerUpgradeFeatureCode = createSelector(
  getPartnerUpgradeTier,
  ProtectedUtils.getPartnerUpgradeFeatureCode,
);

export const getPartnerUpgradePlanName = createSelector(
  getPartnerUpgradeTier,
  PaymentsUtils.getPlanName,
);

export const getPartnerUpgradeUrl = createSelector(
  getPartnerUpgradePartnerName,
  getPartnerUpgradeFeatureCode,
  getPartnerUpgradeEntitlementId,
  ProtectedUtils.getPartnerUpgradeUrl,
);

export const isPartnerBilled = createSelector(
  getPaymentProviderType,
  ProtectedUtils.isEqual(PaymentsConstants.PaymentProviderTypes.PARTNER_BILLED),
);

export const isVendorBilled = createSelector(
  getPaymentProviderType,
  ProtectedUtils.isEqual(PaymentsConstants.PaymentProviderTypes.VENDOR_BILLED),
);

export const isActiveProductOnceBilledTrial = createSelector(
  getInPaymentBackedTrial,
  getActiveProductPrice,
  getActiveProductTimesRenewable,
  ProtectedUtils.isActiveProductOnceBilledTrial,
);

export const isIn6MonthPremiumNACTrial = createSelector(
  getActiveProductGroupTier,
  getActiveProductDiscountType,
  getInPaymentBackedTrial,
  getActiveProductTimesRenewable,
  getActiveProductDurationType,
  ProtectedUtils.isIn6MonthPremiumNACTrial,
);

export const isDirectBilledUser = createSelector(
  isVendorBilled,
  isPartnerBilled,
  isNonPaymentBackedUser,
  PaymentsUtils.isDirectBilledUser,
);

export const is3rdPartyBilled = createSelector(
  isPartnerBilled,
  isVendorBilled,
  ProtectedUtils.is3rdPartyBilled,
);

export const isOffered6monthTrial = createSelector(
  productTierIsPlus,
  is3rdPartyBilled,
  getInPaymentBackedTrial,
  getSelectedTier,
  getSelectedDiscountType,
  state => state,
  (
    _productTierIsPlus,
    _is3rdPartyBilled,
    inPaymentBackedTrial,
    selectedTier,
    selectedDiscountType,
    state,
  ) => {
    const offeringTrial = getOfferPaymentBackedTrial(state, selectedTier);

    return (
      _productTierIsPlus &&
      !_is3rdPartyBilled &&
      !inPaymentBackedTrial &&
      offeringTrial &&
      selectedTier === PaymentsConstants.tiers.PANDORA_PREMIUM &&
      !PaymentsUtils.isDiscount(selectedDiscountType)
    );
  },
);

export const getUpdatedBillingInfo = createSelector(
  getUserSettings,
  ProtectedUtils.getPropFromMap(PaymentsConstants.ReducerKeys.UPDATED_BILLING_INFO),
);

export const hasPaymentInfoOnFile = createSelector(
  paymentProviderIsPaypal,
  hasCreditCardOnFile,
  ProtectedUtils.hasPaymentInfoOnFile,
);

export const createHasDiscountOfferSelector = createSelector(
  getOfferedProductGroups,
  isNonPaymentBackedUser,
  (productGroups, isGiftee) =>
    memoize(discountType => ProtectedUtils.hasDiscountOffer(productGroups, isGiftee, discountType)),
);

export const getPlusMonthlyBackingProduct = createSelector(
  getOfferedProductGroups,
  getPaymentProvider,
  (productGroups, paymentProvider) =>
    ProtectedUtils.findBackingProduct(productGroups, {
      [PaymentsConstants.ProductKeys.PRODUCT_TIER]: PaymentsConstants.tiers.PANDORA_PLUS,
      [PaymentsConstants.ProductKeys.DISCOUNT_TYPE]: PaymentsConstants.discountTypes.NONE,
      [PaymentsConstants.ProductKeys.DURATION_TYPE]: PaymentsConstants.DurationTypes.MONTHLY,
      [PaymentsConstants.ProductKeys.PAYMENT_PROVIDER]: paymentProvider,
    }),
);

export const isUserOfferedPlusLegacyPricing = createSelector(
  getPlusMonthlyBackingProduct,
  ProtectedUtils.isUserOfferedLegacyPricing,
);

export const isSubscriptionSoonToExpire = createSelector(
  getNextBillingDate,
  getAutoRenew,
  isListenerComplimentary,
  isNonPaymentBackedUser,
  ProtectedUtils.isSubscriptionSoonToExpire,
);
export const getMobileMessage = createSelector(
  getUserSettings,
  ProtectedUtils.getPropFromMap(PaymentsConstants.ReducerKeys.MOBILE_MESSAGE),
);

export const getHasSeenPlanDetails = createSelector(
  D.Utils.Route.isOnPaymentsPage,
  D.Utils.Route.isOnVerificationPage,
  ProtectedUtils.getHasSeenPlanDetails,
);

// this gets an array of upgradeCards objects
// [{tier, discountType}, ...]
export const createGetOffersToDisplayArraySelector = createSelector(
  getSelectedTier,
  getSelectedDiscountType,
  (tier, discountType) =>
    memoize(
      isOnPlansPage => {
        return ProtectedUtils.getOffersToDisplayArray(tier, discountType, isOnPlansPage);
      },
      isOnPlansPage => tier + discountType + isOnPlansPage,
    ),
);

export const getActivePlanName = createSelector(
  getActiveProductGroupTier,
  getActiveProductDiscountType,
  PaymentsUtils.getPlanName,
);

export const hasMultipleBackingProducts = createSelector(
  getOfferedProductGroups,
  getSelectedTier,
  getSelectedDiscountType,
  ProtectedUtils.hasMultipleBackingProducts,
);

export const createHasMultipleBackingProductsSelector = createSelector(
  getOfferedProductGroups,
  productGroups =>
    memoize(
      (tier, discountType) =>
        ProtectedUtils.hasMultipleBackingProducts(productGroups, tier, discountType),
      ProtectedUtils.generateMemoKey,
    ),
);

// @todo [WEBCLIENT-7277] get rid of `getAvailablePlans()` - replace with standard selectors
export const getAvailablePlans = createSelector(
  getOfferedProductGroups,
  getPaymentProvider,
  ProtectedUtils.getAvailablePlans,
);

export const createHasOfferSelector = createSelector(getOfferedProductGroups, productGroups =>
  memoize(
    filters => ProtectedUtils.hasOffer(productGroups, filters),
    ProtectedUtils.generateMemoKey,
  ),
);

export const createGetOfferDurationTypeSelector = createSelector(
  getOfferedProductGroups,
  productGroups =>
    memoize(
      (tier, discountType) =>
        ProtectedUtils.getOfferDurationType(productGroups, tier, discountType),
      ProtectedUtils.generateMemoKey,
    ),
);

export const createGetOfferDurationTimeSelector = createSelector(
  getOfferedProductGroups,
  productGroups =>
    memoize(
      (tier, discountType) =>
        ProtectedUtils.getOfferDurationTime(productGroups, tier, discountType),
      ProtectedUtils.generateMemoKey,
    ),
);

export const getSelectedVendorSku = createSelector(
  getOfferedProductGroups,
  getSelectedTier,
  getSelectedDiscountType,
  getSelectedPaymentProvider,
  ProtectedUtils.getVendorSku,
);

export const createGetOfferVendorSkuSelector = createSelector(
  getOfferedProductGroups,
  productGroups =>
    memoize(
      (tier, discountType, paymentProvider) =>
        ProtectedUtils.getVendorSku(productGroups, tier, discountType, paymentProvider),
      ProtectedUtils.generateMemoKey,
    ),
);

export const createGetTrialDurationTypeSelector = createSelector(
  getOfferedProductGroups,
  productGroups =>
    memoize(
      (tier, discountType) =>
        ProtectedUtils.getOfferDurationType(productGroups, tier, discountType, null, true, false),
      ProtectedUtils.generateMemoKey,
    ),
);

export const createGetTrialDurationTimeSelector = createSelector(
  getOfferedProductGroups,
  productGroups =>
    memoize(
      (tier, discountType) =>
        ProtectedUtils.getOfferDurationTime(productGroups, tier, discountType, null, true, false),
      ProtectedUtils.generateMemoKey,
    ),
);

export const createGetTrialTimesRenewableSelector = createSelector(
  getOfferedProductGroups,
  productGroups =>
    memoize(
      (tier, discountType) =>
        ProtectedUtils.getOfferTimesRenewable(productGroups, tier, discountType, null, true, false),
      ProtectedUtils.generateMemoKey,
    ),
);

export const createGetBackingProductPriceSelector = createSelector(
  getOfferedProductGroups,
  productGroups =>
    memoize(
      (tier, discountType, durationType) =>
        ProtectedUtils.getBackingProductPrice(productGroups, tier, discountType, durationType),
      ProtectedUtils.generateMemoKey,
    ),
);

export const createGetBackingProductMonthlyPrice = createSelector(
  getOfferedProductGroups,
  productGroups =>
    memoize(
      (tier, discountType) =>
        ProtectedUtils.getBackingProductPrice(
          productGroups,
          tier,
          discountType,
          PaymentsConstants.DurationTypes.MONTHLY,
        ),
      ProtectedUtils.generateMemoKey,
    ),
);

export const createGetBackingProductYearlyPrice = createSelector(
  getOfferedProductGroups,
  productGroups =>
    memoize(
      (tier, discountType) =>
        ProtectedUtils.getBackingProductPrice(
          productGroups,
          tier,
          discountType,
          PaymentsConstants.DurationTypes.YEARLY,
        ),
      ProtectedUtils.generateMemoKey,
    ),
);

export const createGetOfferBackingProductIdSelector = createSelector(
  getOfferedProductGroups,
  productGroups =>
    memoize(
      (tier, discountType, durationType) =>
        ProtectedUtils.getBackingProductId(productGroups, tier, discountType, durationType),
      ProtectedUtils.generateMemoKey,
    ),
);

export const createGetUpgradeOfferPriceSelector = createSelector(
  getOfferedProductGroups,
  productGroups =>
    memoize(
      (tier, discountType, durationType) =>
        ProtectedUtils.getUpgradeOfferPrice(productGroups, tier, discountType, durationType),
      ProtectedUtils.generateMemoKey,
    ),
);

export const createGetUpgradeOfferDurationTypeSelector = createSelector(
  getOfferedProductGroups,
  productGroups =>
    memoize(
      (tier, discountType) =>
        ProtectedUtils.getUpgradeOfferDurationType(productGroups, tier, discountType),
      ProtectedUtils.generateMemoKey,
    ),
);

export const createGetTrialPriceSelector = createSelector(getOfferedProductGroups, productGroups =>
  memoize(
    (tier, discountType) => ProtectedUtils.getTrialPrice(productGroups, tier, discountType),
    ProtectedUtils.generateMemoKey,
  ),
);

export const createIsOfferedPaidTrialSelector = createSelector(
  getOfferedProductGroups,
  productGroups =>
    memoize(
      (tier, discountType) => ProtectedUtils.isOfferedPaidTrial(productGroups, tier, discountType),
      ProtectedUtils.generateMemoKey,
    ),
);

export const createIsOfferedFreeTrialSelector = createSelector(
  getOfferedProductGroups,
  productGroups =>
    memoize(
      (tier, discountType) => ProtectedUtils.isOfferedFreeTrial(productGroups, tier, discountType),
      ProtectedUtils.generateMemoKey,
    ),
);

export const createIsOfferedTrialSelector = createSelector(getOfferedProductGroups, productGroups =>
  memoize(
    (tier, discountType) => !!ProtectedUtils.findInitialProduct(productGroups, tier, discountType),
    ProtectedUtils.generateMemoKey,
  ),
);

export const createIsOfferedMonthlyBilledTrialSelector = createSelector(
  getOfferedProductGroups,
  productGroups =>
    memoize(
      (tier, discountType) =>
        !!ProtectedUtils.isOfferedMonthlyBilledTrial(productGroups, tier, discountType),
      ProtectedUtils.generateMemoKey,
    ),
);

// the retail price is the price of the backing product if a listener is in a trial
export const getActiveProductRetailPrice = createSelector(
  getOfferedProductGroups,
  getActiveProductGroupTier,
  getActiveProductDiscountType,
  getActiveProductType,
  getSubscriptionDurationType,
  getSubscriptionPrice,
  ProtectedUtils.getActiveProductRetailPrice,
);

// the undiscounted price is the price of the regular product if discountType is NONE
// e.g. the pandora premium price is undiscounted compared to pandora premium student
export const getActiveProductUndiscountedPrice = createSelector(
  getOfferedProductGroups,
  getActiveProductGroupTier,
  getSubscriptionDurationType,
  ProtectedUtils.getUndiscountedPrice,
);

export const hasSelectedPlan = createSelector(
  getSelectedTier,
  getPartnerUpgradeTier,
  getSpecialOfferTier,
  ProtectedUtils.hasSelectedPlan,
);

export const isProcessingPaypalToken = createSelector(
  D.Utils.QueryParams.getToken,
  getIsPaypalComplete,
  ProtectedUtils.isProcessingPaypalToken,
);

export const getFamilyPlanParentEmail = createSelector(
  getUserSettings,
  ProtectedUtils.getPropFromMap(PaymentsConstants.ReducerKeys.FAMILY_PLAN_PARENT_EMAIL),
);

export function isInFamilyPlanInviteeFlow(state) {
  return D.getInDomain(
    'payments.selectors._familyPlanAcceptInvite_.isInFamilyPlanInviteeFlow',
    '',
  )(state);
}

export function getSpecialOfferCode(state) {
  return D.getInDomain('payments.selectors._specialOffers_.getSpecialOfferCode', '')(state);
}

export function getSpecialOfferCampaignId(state) {
  return D.getInDomain('payments.selectors._specialOffers_.getSpecialOfferCampaignId', '')(state);
}

export function getSpecialOfferTier(state) {
  return D.getInDomain('payments.selectors._specialOffers_.getSpecialOfferTier', '')(state);
}

export function getSpecialOfferDiscountType(state) {
  return D.getInDomain('payments.selectors._specialOffers_.getSpecialOfferDiscountType', '')(state);
}

export function isInSpecialOfferFlow(state) {
  return D.getInDomain('payments.selectors._specialOffers_.isInSpecialOfferFlow', false)(state);
}

export function getSpecialOfferPlanName(state) {
  return D.getInDomain('payments.selectors._specialOffers_.getSpecialOfferPlanName', '')(state);
}

export function getSpecialOfferStatus(state) {
  return D.getInDomain('payments.selectors._specialOffers_.getSpecialOfferStatus', '')(state);
}

export function getAuthCampaignCopy(state) {
  return D.getInDomain('payments.selectors._specialOffers_.getAuthCampaignCopy', '')(state);
}

export function getIsDiscountPaymentPageReady(state) {
  return D.getInDomain(
    'payments.selectors._verification_.getIsDiscountPaymentPageReady',
    '',
  )(state);
}

export function isDiscountVerificationStatusVerified(state) {
  return D.getInDomain(
    'payments.selectors._verification_.isDiscountVerificationStatusVerified',
    '',
  )(state);
}

export function isDiscountVerificationInfoLoaded(state) {
  return D.getInDomain(
    'payments.selectors._verification_.isDiscountVerificationInfoLoaded',
    '',
  )(state);
}

export function getDiscountVerificationDiscountType(state) {
  return D.getInDomain(
    'payments.selectors._verification_.getDiscountVerificationDiscountType',
    '',
  )(state);
}

export function isInStudentReverificationPeriod(state) {
  return D.getInDomain(
    'payments.selectors._verification_.isInStudentReverificationPeriod',
    false,
  )(state);
}

export function isStudentReverificationDenied4Year(state) {
  return D.getInDomain(
    'payments.selectors._verification_.isStudentReverificationDenied4Year',
    false,
  )(state);
}
